import { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import { debounce } from "lodash";

import Api from "lib/api/httpRequestHelper";

import {
  Grid,
  Button,
  Container,
  CircularProgress,
  FormGroup,
  FormLabel,
  OutlinedInput,
  InputAdornment,
} from "@material-ui/core";

import AutoCompleteField from "components/_refactor/fields/autocomplete";

import { IsMobile } from "utils/is-mobile";

import themeColorsDesingSystem from "assets/theme/ds-colors";

const statusOptions = [
  { value: "A", label: "A" },
  { value: "C", label: "C" },
  { value: "E", label: "E" },
  { value: "F", label: "F" },
  { value: "L", label: "L" },
  { value: "P", label: "P" },
  { value: "X", label: "X" },
];

function buildQueryString(params) {
  const parts = Object.entries(params).filter(
    ([, value]) => value != null && value !== "",
  );
  if (parts.length === 0) return "";
  return "?" + parts.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
}

export default function Filters({
  handleSearch,
  searching,
  disableImportFile,
  idMarca,
}) {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [commercialNames, setCommercialNames] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState({
    categories: false,
    subcategories: false,
    collections: false,
    commercialNames: false,
    skus: false,
  });
  const [skus, setSkus] = useState([]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      category: null,
      subcategory: null,
      collection: null,
      status: null,
      commercialName: null,
      sku: [],
      importFile: null,
    },
    onSubmit: (values) => {
      handleSearch({ ...values, initializePreSelection: true });
    },
  });

  const isMobile = IsMobile();

  const handleChange = (key, value) => {
    formik.setFieldValue(key, value);
  };

  const handleGetCategories = async () => {
    try {
      setLoadingFilters((prev) => ({ ...prev, categories: true }));
      const query = buildQueryString({ idMarca });
      const response = await Api.get(
        "product-category",
        "product",
        query,
        true,
      );

      setCategories(
        response.Result.ListaCategoria.map((item, index) => ({
          id: index + 1,
          label: item.Descricao,
        })),
      );
    } catch (error) {
      console.error("Erro ao buscar categorias", error);
    } finally {
      setLoadingFilters((prev) => ({ ...prev, categories: false }));
    }
  };

  const handleGetSubcategories = async (category) => {
    setLoadingFilters((prev) => ({ ...prev, subcategories: true }));
    try {
      const query = buildQueryString({
        categoria: category || undefined,
        idMarca,
      });
      const response = await Api.get(
        "product-subcategory",
        "product",
        query,
        true,
      );
      setSubcategories(
        response.Result.ListaSubCategoria.map((item, index) => ({
          id: index + 1,
          label: item.Descricao,
        })),
      );
    } catch (error) {
      console.error("Erro ao buscar subcategorias", error);
    } finally {
      setLoadingFilters((prev) => ({ ...prev, subcategories: false }));
    }
  };

  const handleGetCollections = async () => {
    setLoadingFilters((prev) => ({ ...prev, collections: true }));
    try {
      const query = buildQueryString({ idMarca });
      const response = await Api.get(
        "product-colecao-list",
        "product",
        query,
        true,
      );

      setCollections(
        response.Result.map((item, index) => ({
          id: index + 1,
          label: item,
          value: item,
        })),
      );
    } catch (error) {
      console.error("Erro ao buscar coleções", error);
    } finally {
      setLoadingFilters((prev) => ({ ...prev, collections: false }));
    }
  };

  const handleGetCommercialNames = async (searchTerm = "") => {
    setLoadingFilters((prev) => ({ ...prev, commercialNames: true }));
    try {
      const query = buildQueryString({ idMarca, filter: searchTerm });
      const response = await Api.get(
        "product-nome-produto-edi-list",
        "product",
        query,
        true,
      );

      setCommercialNames(
        response.Result.map((item, index) => ({ id: index + 1, label: item })),
      );
    } catch (error) {
      console.error("Erro ao buscar nomes comerciais", error);
    } finally {
      setLoadingFilters((prev) => ({ ...prev, commercialNames: false }));
    }
  };

  const debouncedGetCommercialNames = useMemo(
    () =>
      debounce((searchTerm) => {
        handleGetCommercialNames(searchTerm);
      }, 300),
    [idMarca],
  );

  const handleGetSkus = async () => {
    setLoadingFilters((prev) => ({ ...prev, skus: true }));
    try {
      const query = buildQueryString({ idMarca });
      const response = await Api.get(
        "product-ean-list",
        "product",
        query,
        true,
      );
      setSkus(
        response.Result.ListaEan.map((item, index) => ({
          id: index + 1,
          label: item,
          value: item,
        })),
      );
    } catch (error) {
      console.error("Erro ao buscar SKUs", error);
    } finally {
      setLoadingFilters((prev) => ({ ...prev, skus: false }));
    }
  };

  useEffect(() => {
    handleGetCategories();
    handleGetCollections();
    handleGetCommercialNames();
    handleGetSkus();
  }, [idMarca]);

  useEffect(() => {
    if (formik.values.category?.label) {
      handleGetSubcategories(formik.values.category.label);
      return;
    }

    setSubcategories([]);
  }, [formik.values.category]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Container maxWidth="100%" style={{ padding: 0, marginTop: "10px" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4} lg={3} xl={2} justifyContent="center">
            <AutoCompleteField
              options={categories}
              label="Categoria do produto"
              optionLabelKey="label"
              optionIdKey="id"
              onChange={(option) => handleChange("category", option)}
              value={formik.values.category}
              disabled={searching || loadingFilters.categories}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3} xl={2} justifyContent="center">
            <AutoCompleteField
              options={subcategories}
              label="Subcategoria do produto"
              optionLabelKey="label"
              optionIdKey="id"
              onChange={(option) => handleChange("subcategory", option)}
              value={formik.values.subcategory}
              disabled={searching || loadingFilters.subcategories}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3} xl={3} justifyContent="center">
            <AutoCompleteField
              options={collections}
              label="Coleção"
              optionLabelKey="label"
              optionIdKey="id"
              onChange={(option) => handleChange("collection", option)}
              value={formik.values.collection}
              disabled={searching || loadingFilters.collections}
            />
          </Grid>
          <Grid item xs={12} md={2} lg={2} xl={1} justifyContent="center">
            <AutoCompleteField
              options={statusOptions}
              label="Status"
              optionLabelKey="label"
              optionIdKey="value"
              onChange={(option) => handleChange("status", option)}
              value={formik.values.status}
              disabled={searching || loadingFilters.status}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={4} xl={5} justifyContent="center">
            <AutoCompleteField
              options={commercialNames}
              label="Nome comercial"
              optionLabelKey="label"
              optionIdKey="id"
              onChange={(option) => handleChange("commercialName", option)}
              onInputChange={(_, value) => debouncedGetCommercialNames(value)}
              value={formik.values.commercialName}
              disabled={searching}
              loading={loadingFilters.commercialNames}
              preserveInputWhenLoading={true}
            />
          </Grid>
          <Grid item xs={12} md={5} lg={5} xl={4} justifyContent="center">
            <AutoCompleteField
              options={skus}
              label="SKU"
              optionLabelKey="label"
              optionIdKey="value"
              multiple={true}
              onChange={(option) => handleChange("sku", option)}
              value={formik.values.sku}
              disabled={searching || loadingFilters.skus}
            />
          </Grid>
          {!disableImportFile && (
            <Grid item xs={12} md={5} lg={5} xl={6} justifyContent="center">
              <FormGroup width="fit-content">
                <FormLabel
                  style={{ color: themeColorsDesingSystem.text.primary }}
                >
                  Importar arquivo de SKU
                </FormLabel>
                <OutlinedInput
                  id="importFile"
                  fullWidth
                  type="file"
                  placeholder="Importar arquivo de SKU"
                  onChange={(event) =>
                    handleChange("importFile", event.target.files?.[0] ?? null)
                  }
                  accept=".csv,.xlsx,.xls,.txt"
                  endAdornment={
                    formik.values.importFile ? (
                      <InputAdornment position="end">
                        <CircularProgress
                          style={{ marginLeft: 5, marginBottom: 1 }}
                          size={16}
                        />
                      </InputAdornment>
                    ) : (
                      ""
                    )
                  }
                />
              </FormGroup>
            </Grid>
          )}
        </Grid>

        <Grid
          container
          spacing={2}
          alignItems="center"
          style={{ marginTop: "20px" }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={8}
            style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
          >
            <Button
              style={
                isMobile
                  ? { width: "100%", margin: "0.75rem 0" }
                  : { width: "200px" }
              }
              color="default"
              variant="contained"
              type="submit"
              disabled={searching}
            >
              {searching ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Consultar"
              )}
            </Button>
          </Grid>
        </Grid>
      </Container>
    </form>
  );
}
